import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  redirect,
  useParams,
} from "@tanstack/react-router";
import type { ReactNode } from "react";
import { AdminShell, AppShell } from "@/components/app-shell";
import { MaintenanceGate } from "@/components/maintenance-gate";
import { AuthPage } from "@/pages/auth-page";
import { DashboardPage } from "@/pages/dashboard-page";
import { SearchPage, WordPage } from "@/pages/search-page";
import { MyWordsPage } from "@/pages/my-words-page";
import { WordLibraryPage } from "@/pages/word-library-page";
import { CapturePage } from "@/pages/capture-page";
import { ReviewPage } from "@/pages/review-page";
import { PracticePage } from "@/pages/practice-page";
import { ProfilePage } from "@/pages/profile-page";
import { WordCollectionPage } from "@/pages/word-collection-page";
import { PoliticsCollectionPage } from "@/pages/politics-collection-page";
import { PoliticsDashboardPage } from "@/pages/politics-dashboard-page";
import { PoliticsHistoryDetailPage, PoliticsHistoryPage } from "@/pages/politics-history-page";
import { PoliticsPracticePage } from "@/pages/politics-practice-page";
import { PoliticsQuestionBankPage } from "@/pages/politics-question-bank-page";
import { isSupportedSubject, subjectForRoute } from "@/lib/subjects";
import {
  AdminAuditPage,
  AdminContentPage,
  AdminOverviewPage,
  AdminUsersPage,
} from "@/pages/admin-pages";
import { AdminSettingsPage } from "@/pages/admin-settings-page";

const rootRoute = createRootRoute({
  component: () => <MaintenanceGate><Outlet /></MaintenanceGate>,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => { throw redirect({ to: "/auth/login" }); },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/login",
  component: () => <AuthPage initialMode="login" />,
});
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/register",
  component: () => <AuthPage initialMode="register" />,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app/$subject",
  component: AppShell,
});

function SubjectDashboardRedirect() {
  const params = useParams({ strict: false }) as { subject?: string };
  return <Navigate to="/app/$subject/dashboard" params={{ subject: subjectForRoute(params.subject) }} replace />;
}

function SubjectDashboard() {
  const params = useParams({ strict: false }) as { subject?: string };
  if (params.subject === "politics") return <PoliticsDashboardPage />;
  if (params.subject === "english") return <DashboardPage />;
  return <SubjectDashboardRedirect />;
}

function EnglishOnly({ children }: { children: ReactNode }) {
  const params = useParams({ strict: false }) as { subject?: string };
  return params.subject === "english" ? children : <SubjectDashboardRedirect />;
}

function PoliticsOnly({ children }: { children: ReactNode }) {
  const params = useParams({ strict: false }) as { subject?: string };
  return params.subject === "politics" ? children : <SubjectDashboardRedirect />;
}

function SharedSubjectPage({ children }: { children: ReactNode }) {
  const params = useParams({ strict: false }) as { subject?: string };
  return isSupportedSubject(params.subject) ? children : <SubjectDashboardRedirect />;
}

const appIndexRoute = createRoute({ getParentRoute: () => appRoute, path: "/", component: SubjectDashboardRedirect });
const dashboardRoute = createRoute({ getParentRoute: () => appRoute, path: "/dashboard", component: SubjectDashboard });
const searchRoute = createRoute({ getParentRoute: () => appRoute, path: "/search", component: () => <EnglishOnly><SearchPage /></EnglishOnly> });
const wordRoute = createRoute({ getParentRoute: () => appRoute, path: "/words/$word", component: () => <EnglishOnly><WordPage /></EnglishOnly> });
const myWordsRoute = createRoute({ getParentRoute: () => appRoute, path: "/my-words", component: () => <EnglishOnly><MyWordsPage /></EnglishOnly> });
const wordLibraryRoute = createRoute({ getParentRoute: () => appRoute, path: "/word-library", component: () => <EnglishOnly><WordLibraryPage /></EnglishOnly> });
const captureRoute = createRoute({ getParentRoute: () => appRoute, path: "/capture", component: () => <EnglishOnly><CapturePage /></EnglishOnly> });
const reviewRoute = createRoute({ getParentRoute: () => appRoute, path: "/review", component: () => <EnglishOnly><ReviewPage /></EnglishOnly> });
const practiceRoute = createRoute({ getParentRoute: () => appRoute, path: "/practice", component: () => <EnglishOnly><PracticePage /></EnglishOnly> });
const profileRoute = createRoute({ getParentRoute: () => appRoute, path: "/profile", component: () => <SharedSubjectPage><ProfilePage /></SharedSubjectPage> });
const favoritesRoute = createRoute({ getParentRoute: () => appRoute, path: "/favorites", component: () => <EnglishOnly><WordCollectionPage kind="favorites" /></EnglishOnly> });
const mistakesRoute = createRoute({ getParentRoute: () => appRoute, path: "/mistakes", component: () => <EnglishOnly><WordCollectionPage kind="mistakes" /></EnglishOnly> });
const politicsBankRoute = createRoute({ getParentRoute: () => appRoute, path: "/question-bank", component: () => <PoliticsOnly><PoliticsQuestionBankPage /></PoliticsOnly> });
const politicsPracticeRoute = createRoute({ getParentRoute: () => appRoute, path: "/practice/$sessionId", component: () => <PoliticsOnly><PoliticsPracticePage /></PoliticsOnly> });
const politicsWrongRoute = createRoute({ getParentRoute: () => appRoute, path: "/wrong-questions", component: () => <PoliticsOnly><PoliticsCollectionPage kind="wrong" /></PoliticsOnly> });
const politicsFavoritesRoute = createRoute({ getParentRoute: () => appRoute, path: "/question-favorites", component: () => <PoliticsOnly><PoliticsCollectionPage kind="favorites" /></PoliticsOnly> });
const politicsHistoryRoute = createRoute({ getParentRoute: () => appRoute, path: "/history", component: () => <PoliticsOnly><PoliticsHistoryPage /></PoliticsOnly> });
const politicsHistoryDetailRoute = createRoute({ getParentRoute: () => appRoute, path: "/history/$sessionId", component: () => <PoliticsOnly><PoliticsHistoryDetailPage /></PoliticsOnly> });

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminShell,
});
const adminIndexRoute = createRoute({ getParentRoute: () => adminRoute, path: "/", component: AdminOverviewPage });
const adminUsersRoute = createRoute({ getParentRoute: () => adminRoute, path: "/users", component: AdminUsersPage });
const adminContentRoute = createRoute({ getParentRoute: () => adminRoute, path: "/content", component: AdminContentPage });
const adminSettingsRoute = createRoute({ getParentRoute: () => adminRoute, path: "/settings", component: AdminSettingsPage });
const adminAuditRoute = createRoute({ getParentRoute: () => adminRoute, path: "/audit", component: AdminAuditPage });

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  appRoute.addChildren([
    appIndexRoute,
    dashboardRoute,
    searchRoute,
    wordRoute,
    myWordsRoute,
    wordLibraryRoute,
    captureRoute,
    reviewRoute,
    practiceRoute,
    profileRoute,
    favoritesRoute,
    mistakesRoute,
    politicsBankRoute,
    politicsPracticeRoute,
    politicsWrongRoute,
    politicsFavoritesRoute,
    politicsHistoryRoute,
    politicsHistoryDetailRoute,
  ]),
  adminRoute.addChildren([adminIndexRoute, adminUsersRoute, adminContentRoute, adminSettingsRoute, adminAuditRoute]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: false,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
